import { type ForwardRefRenderFunction, type ReactNode, forwardRef } from "react";

import {
	type ButtonLevel,
	type ButtonSize,
	type ButtonVariant,
	MomentButton,
} from "../MomentButton";
import { useButtonReducer } from "./useButtonReducer";

export interface ButtonAlphaProps {
	label?: string;
	variant?: ButtonVariant;
	level?: ButtonLevel;
	size?: ButtonSize;
	disabled?: boolean;
	loading?: boolean;
	/** onClick returns an optional promise that triggers the loading state */
	onClick?: unknown;
	reducer?: unknown;
	useSetter?: any;
	children?: ReactNode;
}

const ButtonAlphaBase: ForwardRefRenderFunction<HTMLButtonElement, ButtonAlphaProps> = (
	{
		label,
		variant = "normal",
		level = "secondary",
		size = "md",
		disabled = false,
		onClick,
		reducer,
		useSetter,
		children,
		loading,
		...rest
	},
	ref
) => {
	const { value, isLoading, isDisabled, reducerCallback } = useButtonReducer(
		reducer,
		loading,
		disabled
	);

	useSetter && useSetter(value);

	return (
		<MomentButton
			ref={ref}
			disabled={isDisabled}
			variant={variant}
			level={level}
			size={size}
			loading={isLoading}
			onClick={async (e) => {
				//
				// Reducer.
				//

				await reducerCallback(value);

				//
				// Handle `onClick` just like the browser normally does, not catching errors in
				// invocation.
				//

				if (!onClick) {
					return;
				}
				if (typeof onClick !== "function") {
					console.error("onClick must be a function");
					return;
				}

				onClick(e);
			}}
			// IMPORTANT: Spread props here or `Button` will not work with RadixUI triggers!
			{...rest}
		>
			{label || children}
		</MomentButton>
	);
};

export const ButtonAlpha = forwardRef(ButtonAlphaBase);
